import React, { Component } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import TextField from "react-md/lib/TextFields";
import Button from "react-md/lib/Buttons/Button";
import SelectField from "react-md/lib/SelectFields";
import DatePicker from "react-md/lib/Pickers/DatePickerContainer";
import cloneDeep from "lodash/cloneDeep";
import reduce from "lodash/reduce";
import { countries, idTypes } from "../../../../common/utils";
import { CustomerShape } from "../../../../common/proptypes";
import { email, phone } from "../../../../common/validators";
import { normalizeEmail } from "../../../../common/normalize";

const stripNonDigits = (value) => (value ? value.replace(/\D/g, "") : value);

class Profile extends Component {
  constructor(props) {
    super(props);

    this.state = {
      customer: props.customer,
      formError: {},
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.error = this.error.bind(this);
    this.errorText = this.errorText.bind(this);
    this.isFormValid = this.isFormValid.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    if (
      (!this.props.customer && nextProps.customer) ||
      this.props.customer.key !== nextProps.customer.key
    ) {
      this.setState(() => ({
        customer: nextProps.customer,
      }));
    }
  }

  error(field) {
    if (!(field in this.state.formError)) {
      return false;
    }
    return this.state.formError[field] !== null;
  }

  errorText(field) {
    return this.state.formError[field];
  }

  isFormValid() {
    let fields = Object.keys(this.state.customer);
    fields = fields.filter((field) => field !== "address2" && field !== "key");
    return reduce(
      fields,
      (isValid, field) => isValid && !this.error(field),
      true
    );
  }

  handleChange(field, value) {
    const normalizedValue =
      field === "email"
        ? normalizeEmail(value)
        : field === "phone"
        ? stripNonDigits(value)
        : value;

    this.setState((prevState) => {
      const clonedCustomer = cloneDeep(prevState.customer);
      if (field === "dateOfBirth") {
        clonedCustomer.dateOfBirth = {
          day: moment(value, "MM/DD/YYYY").date(),
          month: moment(value, "MM/DD/YYYY").month() + 1,
          year: moment(value, "MM/DD/YYYY").year(),
        };
      } else {
        clonedCustomer[field] = normalizedValue;
      }

      // a simple validation
      // all fields are required except Address 2
      const clonedFormError = cloneDeep(prevState.formError);
      if (field !== "address2") {
        if (field === "email") {
          if (!normalizedValue) {
            clonedFormError[field] = "Required";
          } else if (email(normalizedValue)) {
            clonedFormError[field] = "Invalid email address";
          } else {
            clonedFormError[field] = null;
          }
        } else if (field === "phone") {
          if (!normalizedValue) {
            clonedFormError[field] = "Required";
          } else if (phone(normalizedValue)) {
            clonedFormError[field] = "Invalid phone number, enter a 10-digit US phone number";
          } else {
            clonedFormError[field] = null;
          }
        } else {
          if (!value) {
            clonedFormError[field] = "Required";
          } else {
            clonedFormError[field] = null;
          }
        }
      } else {
        clonedFormError[field] = null;
      }

      return {
        customer: clonedCustomer,
        formError: clonedFormError,
      };
    });
    this.props.onUpdateUI();
  }

  handleSave() {
    this.props.onSave(this.state.customer);
  }

  render() {
    const { className } = this.props;
    const isFormValid = this.isFormValid();

    const customer = cloneDeep(this.state.customer);
    customer.dateOfBirth = customer.dateOfBirth
      ? `${customer.dateOfBirth.month}/${customer.dateOfBirth.day}/${customer.dateOfBirth.year}`
      : null;

    return (
      <div className={className}>
        <div className="md-grid">
          <h6
            className="md-cell md-cell--12 md-cell--bottom md-title profile-title"
            style={{ marginBottom: 10 }}
          >
            Contact Information
          </h6>
          {/* eslint-disable jsx-a11y/tabindex-no-positive */}
          {/* can't tab to element with tabindex < 0 */}
          <TextField
            id="firstName"
            label="First Name:"
            className="md-cell md-cell--6 md-cell--bottom"
            onChange={(value) => this.handleChange("firstName", value)}
            value={customer.firstName}
            error={this.error("firstName")}
            errorText={this.errorText("firstName")}
            // As elements flow in DOM is not the same tab order so we need to specify tab order
            tabIndex={1}
            required
          />
          <TextField
            id="adress1"
            label="Address 1:"
            className="md-cell md-cell--6 md-cell--bottom"
            onChange={(value) => this.handleChange("address", value)}
            value={customer.address}
            error={this.error("address")}
            errorText={this.errorText("address")}
            required
            tabIndex={5}
          />
          <TextField
            id="lastName"
            label="Last Name:"
            className="md-cell md-cell--6 md-cell--bottom"
            onChange={(value) => this.handleChange("lastName", value)}
            value={customer.lastName}
            error={this.error("lastName")}
            errorText={this.errorText("lastName")}
            required
            tabIndex={2}
          />
          <TextField
            id="address2"
            label="Address 2:"
            className="md-cell md-cell--6 md-cell--bottom"
            onChange={(value) => this.handleChange("address2", value)}
            value={customer.address2}
            tabIndex={6}
          />
          <TextField
            id="email"
            label="Email:"
            className="md-cell md-cell--6 md-cell--bottom"
            onChange={(value) => this.handleChange("email", value)}
            value={customer.email}
            error={this.error("email")}
            errorText={this.errorText("email")}
            required
            tabIndex={3}
          />
          <TextField
            id="city"
            label="City:"
            className="md-cell md-cell--3 md-cell--bottom"
            onChange={(value) => this.handleChange("city", value)}
            value={customer.city}
            error={this.error("city")}
            errorText={this.errorText("city")}
            required
            tabIndex={7}
          />
          <TextField
            id="province"
            label="State / Province:"
            className="md-cell md-cell--3 md-cell--bottom"
            onChange={(value) => this.handleChange("province", value)}
            value={customer.province}
            error={this.error("province")}
            errorText={this.errorText("province")}
            required
            tabIndex={8}
          />
          <TextField
            id="phone"
            label="Phone Number:"
            className="md-cell md-cell--6 md-cell--bottom"
            onChange={(value) => this.handleChange("phone", value)}
            value={customer.phone}
            error={this.error("phone")}
            errorText={this.errorText("phone")}
            required
            tabIndex={4}
          />
          <TextField
            id="postal"
            label="Postal:"
            className="md-cell md-cell--3 md-cell--bottom"
            onChange={(value) => this.handleChange("postal", value)}
            value={customer.postal}
            error={this.error("postal")}
            errorText={this.errorText("postal")}
            required
            type="number"
            tabIndex={9}
          />
          <SelectField
            id="country"
            label="Country:"
            className="md-cell md-cell--3 md-cell--bottom"
            menuItems={countries}
            value={customer.country}
            onChange={(value) => this.handleChange("country", value)}
            error={this.error("country")}
            errorText={this.errorText("country")}
            required
            tabIndex={10}
          />
        </div>
        <div className="md-grid">
          <h6
            className="md-cell md-cell--6 md-cell--bottom md-title profile-title"
            style={{ marginBottom: 10 }}
          >
            Indentification Information
          </h6>
          <h6
            className="md-cell md-cell--6 md-cell--bottom md-title profile-title"
            style={{ marginBottom: 10 }}
          >
            Membership Information
          </h6>
          <DatePicker
            id="dateOfBirth"
            label="Date Of Birth:"
            defaultCalendarMode="year"
            className="md-cell md-cell--6"
            onChange={(value) => this.handleChange("dateOfBirth", value)}
            value={customer.dateOfBirth}
            error={this.error("dateOfBirth")}
            errorText={this.errorText("dateOfBirth")}
            required
            tabIndex={11}
          />
          <TextField
            id="membershipType"
            label="Membership Types:"
            className="md-cell md-cell--6 md-cell--bottom"
            tabIndex={16}
          />
          <SelectField
            id="identifcationType"
            label="Indentification Type:"
            className="md-cell md-cell--6 md-cell--bottom"
            // force new line with margin
            style={{ marginRight: "50%" }}
            menuItems={idTypes}
            onChange={(value) => this.handleChange("idType", value)}
            value={customer.idType}
            error={this.error("idType")}
            errorText={this.errorText("idType")}
            required
            tabIndex={14}
          />
          <TextField
            id="identifcationNumber"
            label="Indentification Number:"
            className="md-cell md-cell--6 md-cell--bottom"
            onChange={(value) => this.handleChange("idNumber", value)}
            value={customer.idNumber}
            error={this.error("idNumber")}
            errorText={this.errorText("idNumber")}
            required
            tabIndex={15}
          />
        </div>
        <div className="md-grid">
          <Button
            raised
            primary
            onClick={this.handleSave}
            disabled={!isFormValid}
            style={{
              marginLeft: "auto",
            }}
            tabIndex={17}
          >
            Save
          </Button>
        </div>
        {/* eslint-enable jsx-a11y/tabindex-no-positive */}
      </div>
    );
  }
}

Profile.propTypes = {
  customer: CustomerShape,
  onUpdateUI: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  className: PropTypes.string,
};

Profile.defaultProps = {
  customer: {},
  className: null,
};

export default Profile;



// WEBPACK FOOTER //
// ./src/customer/components/EditCustomer/Profile/Profile.jsx